import { EligibilityAccount } from '../../../../types/responses/Interoperability';
import { clampPage, pageOf } from '../listPaging';

/**
 * As contas de um nível da hierarquia (a conta, filhos, netos...) e a fatia dela que está
 * na tela.
 *
 * `items` é o nível inteiro e `visible` é a página. A distinção é o ponto: o "Habilitar
 * todos" e o contador do cabeçalho falam do nível, e só o desenho das linhas fala da
 * página.
 */
export type EligibilityGroup = {
  level: number;
  items: EligibilityAccount[];
  visible: EligibilityAccount[];
  /** 1-based, como o paginador do dashboard. */
  page: number;
  paginated: boolean;
};

/**
 * Agrupa por nível, do mais alto para o mais fundo.
 *
 * A ordem dentro do nível é a que veio do servidor — a resposta do CS já chega em BFS a
 * partir da raiz, e reordenar aqui embaralharia irmãos que a pessoa lê juntos.
 */
export const groupByLevel = (
  accounts: EligibilityAccount[],
): { level: number; items: EligibilityAccount[] }[] => {
  const byLevel = new Map<number, EligibilityAccount[]>();

  accounts.forEach((account) => {
    if (!byLevel.has(account.level)) {
      byLevel.set(account.level, []);
    }
    byLevel.get(account.level)!.push(account);
  });

  return Array.from(byLevel.keys())
    .sort((x, y) => x - y)
    .map((level) => ({ level, items: byLevel.get(level)! }));
};

/**
 * Os níveis com a página corrente de cada um recortada (MV2-1812).
 *
 * Paginação no cliente é legítima aqui porque a tela tem **todas** as contas em mãos: a
 * elegibilidade é lida de uma vez do CS, e é desse conjunto completo que saem o delta do
 * salvar e os totais do rodapé. Recortar só o que se desenha mantém a marcação feita numa
 * página valendo quando a pessoa vira para outra.
 *
 * Cada nível pagina por conta própria: são listas independentes na mesma tela, e virar a
 * página dos filhos não tem por que mexer na dos netos.
 */
export const pagedGroups = (
  accounts: EligibilityAccount[],
  pages: Record<number, number>,
  size: number,
): EligibilityGroup[] =>
  groupByLevel(accounts).map(({ level, items }) => {
    // A lista encolhe quando o salvar recarrega: a página pedida pode não existir mais, e
    // seção vazia com paginador cheio parece defeito.
    const page = clampPage(pages[level] || 1, items.length, size);

    return {
      level,
      items,
      visible: pageOf(items, page, size),
      page,
      paginated: items.length > size,
    };
  });

/**
 * Texto comparável: sem acento, sem caixa, sem espaço em volta.
 *
 * Quem procura a conta de Belém raramente digita o acento, e quem digita tem de achar a
 * conta que foi cadastrada sem ele — nomes de tenant vêm digitados à mão dos dois jeitos.
 */
const comparable = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

/**
 * As contas cujo nome contém o termo procurado (MV2-1812).
 *
 * `fallbackName` é o rótulo que a tela desenha quando a conta vem sem nome — a própria
 * conta, tipicamente. Procurar por aquilo que está escrito na linha tem de funcionar, e é
 * a tela que sabe o texto traduzido: por isso ele entra por parâmetro, e não uma tradução
 * dentro desta função.
 */
export const filterByName = (
  accounts: EligibilityAccount[],
  term: string,
  fallbackName: string,
): EligibilityAccount[] => {
  const wanted = comparable(term);

  if (!wanted) {
    return accounts;
  }

  return accounts.filter((account) => comparable(account.name || fallbackName).includes(wanted));
};
